/**
 * Автообновление устаревшей вкладки мессенджера (18.09.2026).
 *
 * registerSW.onNeedRefresh у нас не срабатывает никогда: SW без precache, sw.js
 * между сборками байт-в-байт одинаков. Вкладка владельца сутками жила на старом
 * бандле («нет большого эмодзи», «нет плашки пересылки» — код был выкачен, а
 * вкладка загружена раньше). Сверяем путь entry-скрипта в свежем index.html
 * (nginx отдаёт его no-store) с тем, что загружен в этой вкладке.
 *
 * Устарела → если вкладка скрыта и пользователь ничего не теряет (поле ввода
 * пустое, нет вложений/модалок/записи) — тихий reload; иначе тост с кнопкой
 * «Обновить», а reload случится, когда вкладку свернут. Не чаще раза в 3 минуты.
 */
const CHECK_EVERY_MS = 5 * 60 * 1000;
const RELOAD_GUARD_KEY = "kb-version-reload-at";
const TOAST_ID = "kb-version-toast";
let stale = false;
let started = false;

function loadedEntry(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  if (!el) return null;
  try { return new URL(el.src, location.href).pathname; } catch { return null; }
}

async function latestEntry(): Promise<string | null> {
  const res = await fetch(location.pathname, { cache: "no-store", credentials: "same-origin", headers: { Accept: "text/html" } });
  if (!res.ok) return null;
  const html = await res.text();
  const m = html.match(/<script[^>]+type="module"[^>]+src="([^"]*\/assets\/index-[^"]+\.js)"/);
  if (!m) return null;
  try { return new URL(m[1], location.href).pathname; } catch { return null; }
}

/** Редакторы документов (/pages/word, /pages/sheet, /pages/open…) и всё, что не
 *  лента мессенджера: несохранённые правки живут в памяти вкладки — тихий
 *  reload недопустим, только плашка «Обновить» (25.09.2026). */
function isEditorRoute(): boolean {
  const p = location.pathname;
  const brixRoot = p === "/" && /(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(location.hostname);   // BrixChat: лента в корне
  return p.includes("/pages/") || !(p.includes("/messenger") || p.includes("/chat") || brixRoot);
}

function safeToReload(): boolean {
  if (isEditorRoute()) return false;
  if (document.querySelector("[contenteditable=\"true\"]:focus, .univer-container, .superdoc, [data-kb-editor]")) return false;
  const ta = document.querySelector<HTMLTextAreaElement>("textarea.chat-form-input");
  if (ta && ta.value.trim()) return false;
  if (document.querySelector(".chat-pending-files, .chat-composer-banner, .fwd-modal, .chat-form-rec, [role=\"dialog\"], video:not([paused])")) return false;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < 3 * 60 * 1000) return false;
  } catch { /* ignore */ }
  return true;
}

function doReload(): void {
  try { sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now())); } catch { /* ignore */ }
  location.reload();
}

function showToast(): void {
  if (document.getElementById(TOAST_ID)) return;
  const box = document.createElement("div");
  box.id = TOAST_ID;
  box.style.cssText = "position:fixed;left:50%;bottom:calc(96px + env(safe-area-inset-bottom));transform:translateX(-50%);z-index:100000;"
    + "display:flex;align-items:center;gap:12px;max-width:calc(100vw - 24px);box-sizing:border-box;"
    + "background:rgba(32,33,36,0.94);color:#fff;border-radius:12px;padding:10px 12px 10px 16px;"
    + "font:500 14px/1.3 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;box-shadow:0 6px 24px rgba(0,0,0,0.3);";
  const txt = document.createElement("span");
  txt.textContent = "Вышло обновление мессенджера";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = "Обновить";
  btn.style.cssText = "flex:0 0 auto;border:none;border-radius:8px;padding:7px 12px;background:#4fae4e;color:#fff;font:600 14px/1 inherit;cursor:pointer;";
  btn.addEventListener("click", () => doReload());   // на редакторах beforeunload сам спросит про несохранённое
  box.append(txt, btn);
  document.body.appendChild(box);
}

async function check(): Promise<"fresh" | "stale" | "unknown"> {
  try {
    if (!stale) {
      const cur = loadedEntry();
      const latest = await latestEntry();
      if (!cur || !latest) return "unknown";
      if (cur === latest) return "fresh";
      stale = true;
    }
    if (document.visibilityState === "hidden" && safeToReload()) { doReload(); return "stale"; }
    // На редакторах плашку показываем только когда вкладка видна — чтобы она не
    // мигала поверх документа при возврате; и никогда не перезагружаем сами.
    if (document.visibilityState === "visible") showToast();
    return "stale";
  } catch {
    return "unknown";   // офлайн и т.п. — попробуем в следующий раз
  }
}

export function startVersionWatch(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  window.setTimeout(() => { void check(); }, 60 * 1000);
  window.setInterval(() => { void check(); }, CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", () => { void check(); });
  window.addEventListener("online", () => { void check(); });
  // для приёмки и ручной проверки из консоли
  (window as unknown as { __kbVersionCheck?: () => Promise<string> }).__kbVersionCheck = check;
}
